import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ChevronRight, MessageCircle, FileText } from "lucide-react";
import { services } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";
import { Reveal, Overline } from "@/components/Reveal";
import { openWhatsApp } from "@/lib/whatsapp";

function Breadcrumb() {
  const { t } = useLang();
  return (
    <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs text-steel">
      <Link to="/" className="hover:text-amber">{t.breadcrumbHome}</Link>
      <ChevronRight className="h-3 w-3" />
      <span className="text-ice">{t.nav.services}</span>
    </nav>
  );
}

export default function Servizi() {
  const { t, lang } = useLang();
  const sp = t.servicesPage;

  const infoNeeded = lang === "en"
    ? ["Vehicle type and model", "Pickup and destination", "Vehicle condition", "Loading possibility"]
    : ["Tipo e modello del veicolo", "Partenza e destinazione", "Condizioni del veicolo", "Possibilità di carico"];

  const request = (title) => {
    const msg = lang === "en"
      ? `Hi EMME SERVICE CAR, I'd like to request the service: ${title}.`
      : `Ciao EMME SERVICE CAR, vorrei richiedere il servizio: ${title}.`;
    openWhatsApp(msg);
  };

  return (
    <>
      <Seo
        title={lang === "en" ? "Services | EMME SERVICE CAR" : "Servizi | EMME SERVICE CAR — Soccorso e Trasporto Veicoli"}
        description={sp.subtitle}
        path="/servizi"
      />

      <section className="relative bg-carbon pb-16 pt-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Breadcrumb />
          <Overline>{sp.overline}</Overline>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-ice sm:text-5xl">{sp.title}</h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-steel">{sp.subtitle}</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl gap-12 px-6 pb-24 lg:flex lg:px-8">
        {/* Sticky side nav */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <nav className="sticky top-28 space-y-1" aria-label="Servizi">
            {services.map((s) => {
              const Icon = Icons[s.icon] || Icons.Truck;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`side-nav-${s.id}`}
                  className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-steel transition-colors hover:bg-asphalt hover:text-amber"
                >
                  <Icon className="h-4 w-4" /> {s[lang].title}
                </a>
              );
            })}
          </nav>
        </aside>

        {/* Details */}
        <div className="flex-1 space-y-16">
          {services.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Truck;
            const copy = s[lang];
            return (
              <motion.article
                key={s.id}
                id={s.id}
                data-testid={`service-detail-${s.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
                className="scroll-mt-28 overflow-hidden rounded-2xl border border-edge bg-asphalt"
              >
                <div className={`grid gap-0 md:grid-cols-2 ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                  <div className="relative h-56 md:h-full md:[direction:ltr]">
                    <img src={s.image} alt={copy.title} loading="lazy" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-asphalt/60 to-transparent md:hidden" />
                  </div>
                  <div className="p-6 md:p-8 md:[direction:ltr]">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md border border-edge bg-carbon text-amber">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h2 className="mt-4 font-display text-2xl font-bold text-ice">{copy.title}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-steel">{copy.desc}</p>

                    <div className="mt-5 grid gap-4 sm:grid-cols-2">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-amber">{sp.compatible}</p>
                        <p className="mt-1 text-sm text-steel">
                          {lang === "en" ? "Flatbed / heavy transport (per capacity)" : "Pianale / trasporto pesante (in base alla portata)"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-amber">{sp.infoNeeded}</p>
                        <ul className="mt-1 space-y-0.5 text-sm text-steel">
                          {infoNeeded.map((x) => <li key={x}>• {x}</li>)}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => request(copy.title)}
                        data-testid={`detail-whatsapp-${s.id}`}
                        className="inline-flex items-center gap-2 rounded-md bg-whatsapp px-5 py-2.5 text-xs font-bold uppercase text-carbon hover:bg-whatsapp/90"
                      >
                        <MessageCircle className="h-4 w-4" /> {t.common.requestTransport}
                      </button>
                      <Link
                        to="/contatti#preventivo"
                        data-testid={`detail-quote-${s.id}`}
                        className="inline-flex items-center gap-2 rounded-md border border-amber px-5 py-2.5 text-xs font-bold uppercase text-amber hover:bg-amber/10"
                      >
                        <FileText className="h-4 w-4" /> {t.common.requestQuote}
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </>
  );
}
